import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import MasterLocationSelect from "components/MasterLocationSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";
import { invoiceActionPermission, invoiceFormFieldPermission } from "./invoicePermissions";
import { clientInvoiceAmounts } from "./clientInvoiceAmounts";

const emptyItem = () => ({ description: "", quantity: 0, rate: "0.00" });

const decimalStatus = (value, label, maxIntegerDigits = null) => {
  const text = String(value ?? "").trim();
  if (!text) return { valid: false, message: `${label} is required. Use format 100.00.` };
  if (!/^\d*(?:\.\d*)?$/.test(text) || !/^\d/.test(text)) {
    return { valid: false, message: `${label} can contain only digits and one decimal point. Use format 100.00.` };
  }
  const [integerPart, fractionPart] = text.split(".");
  if (maxIntegerDigits && integerPart.length > maxIntegerDigits) {
    return { valid: false, message: `${label} allows maximum ${maxIntegerDigits} digits before the decimal point. Use format 100.00.` };
  }
  if (fractionPart === undefined || fractionPart.length !== 2) {
    return { valid: false, message: `${label} must contain exactly 2 digits after the decimal point. Use format 100.00.` };
  }
  return { valid: true, message: `Valid format: ${text}` };
};

const exchangeRateValidation = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d{1,3}(?:\.\d{1,7})?$/.test(text)) {
    return { valid: false, message: "Exchange rate must have up to 3 digits before and up to 7 digits after the decimal point." };
  }
  if (!(Number(text) > 0)) {
    return { valid: false, message: "Exchange rate must be greater than zero." };
  }
  return { valid: true, message: `Valid exchange rate: ${text}` };
};
const normalizeDecimalInput = (value) => {
  if (value === "") return "";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : value;
};

const normalizeExchangeRateInput = (value) => String(value ?? "").trim();

const invoiceItem = (item) => ({
  project_id: item.project_id || null,
  project_detail_id: item.project_detail_id || null,
  project_code: item.project_code || "",
  country: item.country || "",
  description: item.description || "",
  quantity: item.quantity || 0,
  rate: item.rate === "" || item.rate === null || item.rate === undefined ? "" : normalizeDecimalInput(item.rate),
  total: item.total === "" || item.total === null || item.total === undefined ? "" : normalizeDecimalInput(item.total),
});
const countryOptions = [{ value: "IN", label: "IN" }, { value: "UAE", label: "UAE" }];
const defaultCurrencyOptions = [
  { value: "USD", label: "USD", code: "USD", symbol: "$" },
  { value: "INR", label: "INR", code: "INR", symbol: "₹" },
  { value: "GBP", label: "GBP", code: "GBP", symbol: "£" },
  { value: "EURO", label: "EURO", code: "EURO", symbol: "€" },
  { value: "AED", label: "AED", code: "AED", symbol: "د.إ" },
  { value: "SGD", label: "SGD", code: "SGD", symbol: "S$" },
];

const mergeCurrencyOptions = (apiOptions = []) => {
  const apiByCode = new Map((apiOptions || []).map((option) => {
    const code = String(option?.code || option?.value || "").trim().toUpperCase();
    return [code, option];
  }));

  return defaultCurrencyOptions.map((fallback) => {
    const apiOption = apiByCode.get(fallback.code) || {};
    return {
      value: fallback.code,
      label: fallback.code,
      code: fallback.code,
      symbol: apiOption.symbol || fallback.symbol,
    };
  });
};
const cellSx = { border: "1px solid #e5e7eb", fontSize: "11px", py: 0.6, px: 0.7 };

const currencyDisplay = (value, codeValue, symbolValue) => {
  const code = String(codeValue || "USD").toUpperCase();
  const symbols = { USD: "$", INR: "₹", GBP: "£", EURO: "€", EUR: "€", AED: "د.إ", SGD: "S$" };
  const symbol = symbols[code] || (symbolValue && symbolValue !== code ? symbolValue : code);
  return `${symbol}${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${code}`;
};

const selectValue = (options, value) => options.find((option) => option.value === value) || null;

function InvoiceEdit({ invoiceType }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission } = useModulePermissions(permissions, userPermission, `invoices.${invoiceType}`);
  const canUpdate = hasModulePermission(invoiceActionPermission(invoiceType, "update"));
  const canReturnToView = hasModulePermission(`invoices.${invoiceType}.pages.view.view`)
    && hasModulePermission(invoiceActionPermission(invoiceType, "view"));
  const [form, setForm] = useState(null);
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [partyLoading, setPartyLoading] = useState(false);
  const [clientOptions, setClientOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState(defaultCurrencyOptions);
  const [projectOptions, setProjectOptions] = useState([]);
  const [taxRule, setTaxRule] = useState({ applicable: false, percentage: 0, rule: "", label: "" });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [validationAlert, setValidationAlert] = useState({ open: false, message: "", color: "error" });
  const [exchangeRateStatus, setExchangeRateStatus] = useState(null);
  const [serverVisibility, setServerVisibility] = useState({});
  const canEditField = (field) => {
    const permissionField = field.replace(/^(billing|shipping)_city$/, "$1_address");
    return hasModulePermission(invoiceFormFieldPermission(invoiceType, "edit", permissionField))
      && serverVisibility[permissionField] !== false;
  };

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/invoices/${invoiceType}/${id}?mode=edit`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.message || "Unable to load invoice.");
        const invoiceData = result.data;
        setOriginalInvoice(invoiceData);
        setServerVisibility(invoiceData.form_visibility || {});
        const initialExchangeRate = invoiceData.exchange_rate === null || invoiceData.exchange_rate === undefined || invoiceData.exchange_rate === ""
          ? "1.00"
          : normalizeExchangeRateInput(invoiceData.exchange_rate);
        setForm({
          ...invoiceData,
          exchange_rate: initialExchangeRate,
          cgst_amount: normalizeDecimalInput(invoiceData.cgst_amount ?? 0),
          sgst_amount: normalizeDecimalInput(invoiceData.sgst_amount ?? 0),
          igst_amount: normalizeDecimalInput(invoiceData.igst_amount ?? 0),
          project_ids: (invoiceData.project_ids || []).map(Number),
          items: (invoiceData.items || []).map(invoiceItem),
        });
        setExchangeRateStatus(exchangeRateValidation(initialExchangeRate));

        if (invoiceType === "client") {
          const optionsResponse = await fetch(`${API_URL}/invoices/client/form-options?mode=edit`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
            signal: controller.signal,
          });
          const optionsResult = await optionsResponse.json();
          if (!optionsResponse.ok || !optionsResult.success) throw new Error(optionsResult.message || "Unable to load client options.");
          setServerVisibility((current) => ({ ...current, ...(optionsResult.data?.visibility || {}) }));
          const availableClients = optionsResult.data?.clients || [];
          setClientOptions(availableClients.some((option) => Number(option.value) === Number(invoiceData.client_id))
            ? availableClients
            : [...availableClients, { value: Number(invoiceData.client_id), label: invoiceData.party?.name || invoiceData.billing_name || `Client ${invoiceData.client_id}` }]);
          const mergedCurrencyOptions = mergeCurrencyOptions(optionsResult.data?.currencies);
          setCurrencyOptions(mergedCurrencyOptions);
          setForm((current) => {
            const currencyCode = String(current?.currency_code || "USD").toUpperCase();
            const currencyOption = mergedCurrencyOptions.find((option) => option.code === currencyCode);
            return current ? { ...current, currency_symbol: current.currency_symbol || currencyOption?.symbol || null } : current;
          });

          if (invoiceData.client_id) {
            const detailResponse = await fetch(`${API_URL}/invoices/client/party/${invoiceData.client_id}/form-details?mode=edit`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
              signal: controller.signal,
            });
            const detailResult = await detailResponse.json();
            if (!detailResponse.ok || !detailResult.success) throw new Error(detailResult.message || "Unable to load project options.");
            setServerVisibility((current) => ({ ...current, ...(detailResult.data?.visibility || {}) }));
            const availableProjects = detailResult.data?.projects || [];
            const existingProjects = (invoiceData.project_ids || []).map((projectId, index) => ({
              value: Number(projectId),
              label: (invoiceData.project_codes || [])[index] || String(projectId),
              project_code: (invoiceData.project_codes || [])[index] || String(projectId),
            }));
            setProjectOptions([
              ...existingProjects,
              ...availableProjects.filter((option) => !existingProjects.some((existing) => Number(existing.value) === Number(option.value))),
            ]);
            setTaxRule(detailResult.data?.tax || { applicable: false, percentage: 0, rule: "", label: "" });
          }
        }
      } catch (requestError) {
        if (requestError.name !== "AbortError") setError(requestError.message || "Unable to load invoice.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [id, invoiceType]);

  const subtotal = useMemo(() => (form?.items || []).reduce(
    (sum, item) => sum + (invoiceType === "client" ? Number(item.total || 0) : (Number(item.quantity) || 0) * (Number(item.rate) || 0)),
    0
  ), [form?.items, invoiceType]);
  const totalCompletes = useMemo(
    () => (form?.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0),
    [form?.items]
  );
  const clientTotals = useMemo(
    () => clientInvoiceAmounts(subtotal, form?.exchange_rate, form?.tax_enabled, taxRule, form?.currency_code || "USD"),
    [form?.currency_code, form?.exchange_rate, form?.tax_enabled, subtotal, taxRule]
  );
  const taxTotal = invoiceType === "client"
    ? clientTotals.tax
    : form?.tax_enabled ? Number(form.cgst_amount || 0) + Number(form.sgst_amount || 0) + Number(form.igst_amount || 0) : 0;
  const total = invoiceType === "client" ? clientTotals.total : subtotal + taxTotal;

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const showDecimalFeedback = (value, label, maxIntegerDigits = null) => {
    const status = decimalStatus(value, label, maxIntegerDigits);
    setValidationAlert({ open: true, message: status.message, color: status.valid ? "success" : "error" });
    return status;
  };
  const validateExchangeRate = (value) => {
    const status = exchangeRateValidation(value);
    setExchangeRateStatus(status);
    return status;
  };
  const selectCurrency = (option) => {
    const code = String(option?.code || option?.value || "USD").toUpperCase();
    setForm((current) => ({
      ...current,
      currency_code: code,
      currency_symbol: option?.symbol || null,
      exchange_rate: code === "USD" && (!current.exchange_rate || Number(current.exchange_rate) === 0)
        ? "1.00"
        : current.exchange_rate,
    }));
  };
  const setItem = (index, key, value) => setForm((current) => ({
    ...current,
    items: current.items.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const next = { ...item, [key]: value };
      if (invoiceType === "client" && (key === "quantity" || key === "rate")) {
        next.total = ((Number(next.quantity) || 0) * (Number(next.rate) || 0)).toFixed(2);
      }
      return next;
    }),
  }));
  const removeItem = (index) => setForm((current) => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }));
  const addItemAfter = (index) => setForm((current) => ({
    ...current,
    items: [
      ...current.items.slice(0, index + 1),
      invoiceType === "client" ? invoiceItem({}) : emptyItem(),
      ...current.items.slice(index + 1),
    ],
  }));

  const toggleSameAsBilling = (checked) => setForm((current) => ({
    ...current,
    shipping_same_as_billing: checked,
    ...(checked ? {
      shipping_name: current.billing_name,
      shipping_email: current.billing_email,
      shipping_address: current.billing_address,
      shipping_city: current.billing_city,
      shipping_state: current.billing_state,
      shipping_state_id: current.billing_state_id,
      shipping_postal_code: current.billing_postal_code,
      shipping_country: current.billing_country,
      shipping_country_id: current.billing_country_id,
    } : {}),
  }));

  const chooseClient = async (option) => {
    const clientId = Number(option?.value);
    if (!clientId || clientId === Number(form.client_id)) return;
    setPartyLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/invoices/client/party/${clientId}/form-details?mode=edit`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}`, Accept: "application/json" },
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to load client details.");
      const details = result.data || {};
      if (details.company_warning) throw new Error(details.company_warning);
      if (details.invoicing_company && form.invoicing_company_id
        && Number(details.invoicing_company.id) !== Number(form.invoicing_company_id)) {
        throw new Error("This client uses a different invoicing company. The issuer cannot be changed after the invoice number is generated.");
      }
      const existingProjects = clientId === Number(originalInvoice?.client_id)
        ? (originalInvoice.project_ids || []).map((projectId, index) => ({
          value: Number(projectId),
          label: (originalInvoice.project_codes || [])[index] || String(projectId),
          project_code: (originalInvoice.project_codes || [])[index] || String(projectId),
        })) : [];
      setProjectOptions([
        ...existingProjects,
        ...(details.projects || []).filter((project) => !existingProjects.some((existing) => Number(existing.value) === Number(project.value))),
      ]);
      setServerVisibility((current) => ({ ...current, ...(details.visibility || {}) }));
      setTaxRule(details.tax || { applicable: false, percentage: 0, rule: "", label: "" });
      const currencyCode = String(details.currency?.code || "USD").toUpperCase();
      const currency = currencyOptions.find((item) => item.code === currencyCode);
      const exchangeRate = currencyCode === "USD" ? "1.00" : "";
      const billing = Object.fromEntries(
        ["name", "email", "address", "city", "state", "state_id", "postal_code", "country", "country_id"]
          .map((key) => [`billing_${key}`, details.billing?.[key] ?? ""])
      );
      setForm((current) => ({
        ...current, ...billing, client_id: clientId, project_ids: [], items: [],
        currency_code: currencyCode, currency_symbol: details.currency?.symbol || currency?.symbol || null,
        exchange_rate: exchangeRate, tax_enabled: true,
        invoice_country: details.invoicing_company?.country || current.invoice_country,
        ...(current.shipping_same_as_billing ? Object.fromEntries(
          Object.entries(billing).map(([key, value]) => [key.replace(/^billing_/, "shipping_"), value])
        ) : {}),
      }));
      setExchangeRateStatus(exchangeRate ? exchangeRateValidation(exchangeRate) : null);
    } catch (requestError) {
      setError(requestError.message || "Unable to load client details.");
    } finally {
      setPartyLoading(false);
    }
  };

  const chooseProjects = async (options) => {
    const projectIds = (options || []).map((item) => Number(item.value));
    setField("project_ids", projectIds);
    if (!form.client_id || projectIds.length === 0) {
      setForm((current) => ({ ...current, items: [] }));
      return;
    }

    setPartyLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/invoices/client/project-lines?mode=edit`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ client_id: form.client_id, project_ids: projectIds, invoice_id: Number(id) }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        const validationMessage = result.errors ? Object.values(result.errors).flat()[0] : null;
        throw new Error(validationMessage || result.message || "Unable to load project invoice rows.");
      }
      setServerVisibility((current) => ({ ...current, ...(result.data?.visibility || {}) }));
      setForm((current) => ({ ...current, items: (result.data?.lines || []).map(invoiceItem) }));
    } catch (requestError) {
      setError(requestError.message || "Unable to load project invoice rows.");
    } finally {
      setPartyLoading(false);
    }
  };

  const submit = async () => {
    if (!canUpdate || !form) return;
    if (invoiceType === "client" && (!form.client_id || !form.project_ids?.length)) {
      setError("Select a client and at least one eligible project code.");
      return;
    }
    const currencyCode = String(form.currency_code || "USD").toUpperCase();
    if (invoiceType === "client" || String(form.exchange_rate ?? "") !== "") {
      const exchangeValidation = validateExchangeRate(form.exchange_rate);
      if (!exchangeValidation.valid) {
        setError(exchangeValidation.message);
        setValidationAlert({ open: true, message: exchangeValidation.message, color: "error" });
        return;
      }
    }
    const invalidItem = (form.items || []).find((item) => (
      !decimalStatus(item.rate, invoiceType === "client" ? "Client CPI" : "Rate / CPI").valid
      || (invoiceType === "client" && !decimalStatus(item.total, "Amount").valid)
    ));
    if (invalidItem) {
      const messageText = invoiceType === "client"
        ? "Every Client CPI and Amount must use exactly 2 decimal places, for example 100.00."
        : "Every amount and rate must use exactly 2 decimal places, for example 100.00.";
      setError(messageText);
      setValidationAlert({ open: true, message: messageText, color: "error" });
      return;
    }
    if (invoiceType === "vendor" && form.tax_enabled) {
      const invalidTax = [
        ["cgst_amount", "CGST amount"],
        ["sgst_amount", "SGST amount"],
        ["igst_amount", "IGST amount"],
      ].find(([key]) => !decimalStatus(form[key], key.replace("_amount", "").toUpperCase() + " amount").valid);
      if (invalidTax) {
        const messageText = `${invalidTax[1]} must use exactly 2 decimal places, for example 100.00.`;
        setError(messageText);
        setValidationAlert({ open: true, message: messageText, color: "error" });
        return;
      }
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        client_id: invoiceType === "client" ? form.client_id : undefined,
        project_ids: invoiceType === "client" ? form.project_ids : undefined,
        project_code: invoiceType === "vendor" ? form.project_code || null : undefined,
        reference_number: form.reference_number || null,
        hsn_code: form.hsn_code || null,
        invoice_date: form.invoice_date,
        due_date: form.due_date || null,
        invoice_country: form.invoice_country || null,
        billing_name: form.billing_name || null,
        billing_email: form.billing_email || null,
        billing_address: form.billing_address || null,
        ...(invoiceType === "client" ? { billing_city: form.billing_city || null } : {}),
        billing_state: form.billing_state || null,
        billing_state_id: form.billing_state_id || null,
        billing_postal_code: form.billing_postal_code || null,
        billing_country: form.billing_country || null,
        billing_country_id: form.billing_country_id || null,
        shipping_name: form.shipping_name || null,
        shipping_email: form.shipping_email || null,
        shipping_address: form.shipping_address || null,
        ...(invoiceType === "client" ? { shipping_city: form.shipping_city || null } : {}),
        shipping_state: form.shipping_state || null,
        shipping_state_id: form.shipping_state_id || null,
        shipping_postal_code: form.shipping_postal_code || null,
        shipping_country: form.shipping_country || null,
        shipping_country_id: form.shipping_country_id || null,
        shipping_same_as_billing: invoiceType === "client" ? Boolean(form.shipping_same_as_billing) : undefined,
        currency_code: form.currency_code || "USD",
        currency_symbol: form.currency_symbol || null,
        exchange_rate: form.exchange_rate === "" ? null : form.exchange_rate,
        tax_enabled: Boolean(form.tax_enabled),
        cgst_amount: Number(form.cgst_amount || 0),
        sgst_amount: Number(form.sgst_amount || 0),
        igst_amount: Number(form.igst_amount || 0),
        notes: form.notes || null,
        items: form.items
          .filter((item) => invoiceType === "client"
            ? String(item.project_code || "").trim() !== "" && String(item.country || "").trim() !== ""
            : String(item.description || "").trim() !== "")
          .map((item) => invoiceType === "client" ? {
            project_id: item.project_id || null,
            project_detail_id: item.project_detail_id || null,
            project_code: item.project_code,
            country: item.country,
            description: item.description || null,
            quantity: Number(item.quantity || 0),
            rate: Number(item.rate || 0),
            total: Number(item.total || 0),
          } : {
            description: item.description,
            quantity: Number(item.quantity || 0),
            rate: Number(item.rate || 0),
          }),
      };
      const response = await fetch(`${API_URL}/invoices/${invoiceType}/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}`, "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        const validationMessage = result.errors ? Object.values(result.errors).flat()[0] : null;
        throw new Error(validationMessage || result.message || "Unable to update invoice.");
      }
      navigate(`/invoices/${invoiceType}`, {
        replace: true,
        state: {
          invoiceSuccessMessage: result.message || "Invoice updated successfully.",
        },
      });
    } catch (requestError) {
      setError(requestError.message || "Unable to update invoice.");
    } finally {
      setSaving(false);
    }
  };

  const partyLabel = invoiceType === "client" ? "Client" : "Vendor";
  const fieldBox = { minWidth: 0 };
  const selectedClient = clientOptions.find((option) => Number(option.value) === Number(form?.client_id)) || null;
  const selectedProjects = (form?.project_ids || [])
    .map((projectId) => projectOptions.find((option) => Number(option.value) === Number(projectId)))
    .filter(Boolean);
  const visibleEditItemColumnCount = (invoiceType === "client"
    ? ["item_project_code", "item_country", "item_description", "item_quantity", "item_rate", "item_amount", "add_remove_items"]
    : ["item_serial", "item_description", "item_quantity", "item_rate", "item_amount", "add_remove_items"]
  ).filter(canEditField).length;
  const billingFieldDefinitions = [["billing_name", "Name / Company"], ["billing_email", "Email"], ["billing_address", "Address"], ...(invoiceType === "client" ? [["billing_city", "City / Location"]] : []), ["billing_state", "State"], ["billing_postal_code", "Pincode"], ["billing_country", "Country"]];
  const shippingFieldDefinitions = [["shipping_name", "Name / Company"], ["shipping_email", "Email"], ["shipping_address", "Address"], ...(invoiceType === "client" ? [["shipping_city", "City / Location"]] : []), ["shipping_state", "State"], ["shipping_postal_code", "Pincode"], ["shipping_country", "Country"]];
  const visibleBillingFields = billingFieldDefinitions.filter(([key]) => canEditField(key));
  const visibleShippingFields = shippingFieldDefinitions.filter(([key]) => canEditField(key));
  const showShippingSection = canEditField("shipping_same_as_billing") || visibleShippingFields.length > 0;
  const showCalculationSection = ["billing_currency", "currency_symbol", "exchange_rate", "tax", "usd_amount", "final_amount"].some(canEditField);
  const invoiceMonthMinimum = form?.invoice_month ? `${form.invoice_month}-01` : undefined;
  const invoiceMonthMaximum = useMemo(() => {
    if (!form?.invoice_month) return undefined;
    const [year, month] = form.invoice_month.split("-").map(Number);
    return `${form.invoice_month}-${String(new Date(year, month, 0).getDate()).padStart(2, "0")}`;
  }, [form?.invoice_month]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={2}>
        {loading ? <SoftBox py={8} display="flex" justifyContent="center"><CircularProgress color="info" /></SoftBox> : !form ? <SoftTypography color="error">{error || "Invoice not found."}</SoftTypography> : (
          <Card sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <SoftTypography variant="h4">Edit {partyLabel} Invoice</SoftTypography>
              {canReturnToView && <SoftButton size="small" color="secondary" onClick={() => navigate(`/invoices/${invoiceType}/${id}/view`)}><Icon>arrow_back</Icon>&nbsp; Back</SoftButton>}
            </SoftBox>

            {invoiceType === "client" && (
              <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "3fr 1fr" }} gap={1.25} mb={1.5}>
                {canEditField("project_codes") && <SoftBox>
                  <SoftTypography variant="caption">Project Codes * (same client only)</SoftTypography>
                  <SoftSelect value={selectedProjects} options={projectOptions} isMulti isSearchable isDisabled={saving || partyLoading} isLoading={partyLoading} onChange={chooseProjects} />
                </SoftBox>}
                {canEditField("reference") && <SoftBox>
                  <SoftTypography variant="caption">Client PO</SoftTypography>
                  <SoftInput value={form.reference_number || ""} onChange={(event) => setField("reference_number", event.target.value)} />
                </SoftBox>}
              </SoftBox>
            )}

            <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(6, minmax(0, 1fr))" }} gap={1} mb={2}>
              {canEditField("invoice_number") && <SoftBox sx={fieldBox}><SoftTypography variant="caption">Invoice Number</SoftTypography><SoftInput value={form.invoice_number || ""} disabled /></SoftBox>}
              {invoiceType === "vendor" && canEditField("project_code") && <SoftBox sx={fieldBox}><SoftTypography variant="caption">Project Code</SoftTypography><SoftInput value={form.project_code || ""} onChange={(event) => setField("project_code", event.target.value)} /></SoftBox>}
              {canEditField("invoice_country") && <SoftBox sx={fieldBox}><SoftTypography variant="caption">Invoice Country</SoftTypography><SoftSelect value={selectValue(countryOptions, form.invoice_country)} options={countryOptions} isClearable isDisabled={invoiceType === "client"} onChange={(value) => setField("invoice_country", value?.value || "")} /></SoftBox>}
              {canEditField("invoice_date") && <SoftBox sx={fieldBox}><SoftTypography variant="caption">Invoice Date</SoftTypography><SoftInput type="date" value={form.invoice_date || ""} inputProps={invoiceType === "client" ? { min: invoiceMonthMinimum, max: invoiceMonthMaximum } : undefined} onChange={(event) => setField("invoice_date", event.target.value)} /></SoftBox>}
              {canEditField("due_date") && <SoftBox sx={fieldBox}><SoftTypography variant="caption">Due Date</SoftTypography><SoftInput type="date" value={form.due_date || ""} onChange={(event) => setField("due_date", event.target.value)} /></SoftBox>}
              {canEditField("invoice_month") && <SoftBox sx={fieldBox}><SoftTypography variant="caption">Invoice Month</SoftTypography><SoftInput value={(form.invoice_date || "").slice(0, 7)} disabled /></SoftBox>}
            </SoftBox>

            {canEditField("party") && <SoftBox mb={2}>
              <SoftTypography variant="caption">{partyLabel}</SoftTypography>
              {invoiceType === "client" ? (
                <SoftSelect value={selectedClient} options={clientOptions} isSearchable isDisabled={saving || partyLoading} isLoading={partyLoading} onChange={chooseClient} />
              ) : (
                <SoftInput value={form.party?.name || ""} disabled />
              )}
            </SoftBox>}

            {(visibleBillingFields.length > 0 || showShippingSection) && <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb={2}>
              {visibleBillingFields.length > 0 && <SoftBox>
                <SoftTypography variant="h6" mb={0.75}>Billing To {partyLabel}</SoftTypography>
                {visibleBillingFields.filter(([key]) => !["billing_state", "billing_country"].includes(key)).map(([key, label]) => <SoftBox key={key} mb={0.7}><SoftTypography variant="caption">{label}</SoftTypography><SoftInput value={form[key] || ""} disabled={invoiceType === "client"} onChange={(event) => setField(key, event.target.value)} /></SoftBox>)}
                <MasterLocationSelect
                  countryId={form.billing_country_id}
                  countryName={form.billing_country}
                  stateId={form.billing_state_id}
                  stateName={form.billing_state}
                  disabled={invoiceType === "client"}
                  showCountry={canEditField("billing_country")}
                  showState={canEditField("billing_state")}
                  onCountryChange={({ id: countryId, name }) => setForm((current) => ({
                    ...current,
                    billing_country_id: countryId,
                    billing_country: name,
                    billing_state_id: null,
                    billing_state: "",
                  }))}
                  onStateChange={({ id: stateId, name }) => setForm((current) => ({
                    ...current,
                    billing_state_id: stateId,
                    billing_state: name,
                  }))}
                />
              </SoftBox>}
              {showShippingSection && <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                  <SoftTypography variant="h6" mb={0.75}>Shipping To {partyLabel}</SoftTypography>
                  {invoiceType === "client" && canEditField("shipping_same_as_billing") && <FormControlLabel control={<Checkbox size="small" checked={Boolean(form.shipping_same_as_billing)} onChange={(event) => toggleSameAsBilling(event.target.checked)} />} label="Same as Billing" />}
                </SoftBox>
                {visibleShippingFields.filter(([key]) => !["shipping_state", "shipping_country"].includes(key)).map(([key, label]) => <SoftBox key={key} mb={0.7}><SoftTypography variant="caption">{label}</SoftTypography><SoftInput value={form[key] || ""} disabled={invoiceType === "client" && Boolean(form.shipping_same_as_billing)} onChange={(event) => setField(key, event.target.value)} /></SoftBox>)}
                <MasterLocationSelect
                  countryId={form.shipping_country_id}
                  countryName={form.shipping_country}
                  stateId={form.shipping_state_id}
                  stateName={form.shipping_state}
                  disabled={invoiceType === "client" && Boolean(form.shipping_same_as_billing)}
                  showCountry={canEditField("shipping_country")}
                  showState={canEditField("shipping_state")}
                  onCountryChange={({ id: countryId, name }) => setForm((current) => ({
                    ...current,
                    shipping_country_id: countryId,
                    shipping_country: name,
                    shipping_state_id: null,
                    shipping_state: "",
                  }))}
                  onStateChange={({ id: stateId, name }) => setForm((current) => ({
                    ...current,
                    shipping_state_id: stateId,
                    shipping_state: name,
                  }))}
                />
              </SoftBox>}
            </SoftBox>}

            <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: invoiceType === "client" ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))" }} gap={1} mb={1.2}>
              {invoiceType === "vendor" && canEditField("reference") && <SoftBox><SoftTypography variant="caption">Vendor Reference</SoftTypography><SoftInput value={form.reference_number || ""} onChange={(event) => setField("reference_number", event.target.value)} /></SoftBox>}
              {canEditField("hsn_code") && <SoftBox><SoftTypography variant="caption">HSN Code</SoftTypography><SoftInput value={form.hsn_code || ""} disabled={invoiceType === "client"} onChange={(event) => setField("hsn_code", event.target.value)} /></SoftBox>}
              {canEditField("notes") && <SoftBox><SoftTypography variant="caption">Notes</SoftTypography><SoftInput value={form.notes || ""} onChange={(event) => setField("notes", event.target.value)} /></SoftBox>}
            </SoftBox>

            {canEditField("items_section") && <TableContainer sx={{ border: "1px solid #e5e7eb", mb: 1.5 }}>
              <Table size="small">
                <TableHead sx={{ display: "table-header-group" }}>
                  {invoiceType === "client" ? (
                    <TableRow>{canEditField("item_project_code") && <TableCell sx={cellSx}>Project Code</TableCell>}{canEditField("item_country") && <TableCell sx={cellSx}>Country</TableCell>}{canEditField("item_description") && <TableCell sx={cellSx}>Description</TableCell>}{canEditField("item_quantity") && <TableCell sx={cellSx}>Total Complete</TableCell>}{canEditField("item_rate") && <TableCell sx={cellSx}>Client CPI (USD)</TableCell>}{canEditField("item_amount") && <TableCell sx={cellSx}>Amount (USD)</TableCell>}{canEditField("add_remove_items") && <TableCell sx={cellSx}>Action</TableCell>}</TableRow>
                  ) : (
                    <TableRow>{canEditField("item_serial") && <TableCell sx={cellSx}>S.No</TableCell>}{canEditField("item_description") && <TableCell sx={cellSx}>Description</TableCell>}{canEditField("item_quantity") && <TableCell sx={cellSx}>Completes</TableCell>}{canEditField("item_rate") && <TableCell sx={cellSx}>Rate / CPI</TableCell>}{canEditField("item_amount") && <TableCell sx={cellSx}>Total</TableCell>}{canEditField("add_remove_items") && <TableCell sx={cellSx}>Action</TableCell>}</TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {form.items.map((item, index) => invoiceType === "client" ? (
                    <TableRow key={index}>
                      {canEditField("item_project_code") && <TableCell sx={cellSx}><SoftInput value={item.project_code || ""} onChange={(event) => setItem(index, "project_code", event.target.value)} /></TableCell>}
                      {canEditField("item_country") && <TableCell sx={cellSx}><SoftInput value={item.country || ""} onChange={(event) => setItem(index, "country", event.target.value)} /></TableCell>}
                      {canEditField("item_description") && <TableCell sx={cellSx}><SoftInput value={item.description || ""} placeholder="Description" onChange={(event) => setItem(index, "description", event.target.value)} /></TableCell>}
                      {canEditField("item_quantity") && <TableCell sx={cellSx}><SoftInput type="number" value={item.quantity} inputProps={{ min: 0, step: 1 }} onChange={(event) => setItem(index, "quantity", event.target.value)} /></TableCell>}
                      {canEditField("item_rate") && <TableCell sx={cellSx}>
                        <SoftInput type="text" value={item.rate} inputProps={{ inputMode: "decimal" }} icon={{ component: "$", direction: "left" }} onChange={(event) => { const value = event.target.value; setItem(index, "rate", value); showDecimalFeedback(value, invoiceType === "client" ? "Client CPI" : "Rate / CPI"); }} onBlur={() => { const value = normalizeDecimalInput(item.rate); setItem(index, "rate", value); showDecimalFeedback(value, invoiceType === "client" ? "Client CPI" : "Rate / CPI"); }} />
                      </TableCell>}
                      {canEditField("item_amount") ? (
                        <TableCell sx={cellSx}>
                          <SoftInput type="text" value={item.total} inputProps={{ inputMode: "decimal" }} icon={{ component: "$", direction: "left" }} onChange={(event) => { const value = event.target.value; setItem(index, "total", value); showDecimalFeedback(value, "Amount"); }} onBlur={() => { const value = normalizeDecimalInput(item.total); setItem(index, "total", value); showDecimalFeedback(value, "Amount"); }} />
                        </TableCell>
                      ) : null}
                      {canEditField("add_remove_items") && <TableCell sx={cellSx}>
                        <IconButton color="info" title="Add blank row below" onClick={() => addItemAfter(index)}><Icon>add_circle</Icon></IconButton>
                        <IconButton color="error" title="Delete row" onClick={() => removeItem(index)}><Icon>delete</Icon></IconButton>
                      </TableCell>}
                    </TableRow>
                  ) : (
                    <TableRow key={index}>{canEditField("item_serial") && <TableCell sx={cellSx}>{index + 1}</TableCell>}{canEditField("item_description") && <TableCell sx={cellSx}><SoftInput value={item.description} onChange={(event) => setItem(index, "description", event.target.value)} /></TableCell>}{canEditField("item_quantity") && <TableCell sx={cellSx}><SoftInput type="number" value={item.quantity} onChange={(event) => setItem(index, "quantity", event.target.value)} /></TableCell>}{canEditField("item_rate") && <TableCell sx={cellSx}><SoftInput type="number" value={item.rate} onChange={(event) => setItem(index, "rate", event.target.value)} /></TableCell>}{canEditField("item_amount") && <TableCell sx={cellSx}>{((Number(item.quantity) || 0) * (Number(item.rate) || 0)).toFixed(2)}</TableCell>}{canEditField("add_remove_items") && <TableCell sx={cellSx}><IconButton color="error" onClick={() => removeItem(index)}><Icon>delete</Icon></IconButton></TableCell>}</TableRow>
                  ))}
                  {form.items.length === 0 && <TableRow><TableCell colSpan={Math.max(visibleEditItemColumnCount, 1)} align="center" sx={cellSx}>No line item added yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>}
            {invoiceType === "client" ? (
              <SoftBox display="flex" justifyContent="flex-end" gap={3} mb={1} px={1}>
                {canEditField("total_quantity") && <SoftTypography variant="button" fontWeight="bold">Overall Completes: {totalCompletes.toLocaleString()}</SoftTypography>}
                {canEditField("usd_amount") && <SoftTypography variant="button" color="info" fontWeight="bold">Total Amount: ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</SoftTypography>}
              </SoftBox>
            ) : (
              canEditField("add_remove_items") && <SoftButton size="small" color="info" variant="outlined" onClick={() => setForm((current) => ({ ...current, items: [...current.items, emptyItem()] }))}><Icon>add</Icon>&nbsp; Add Line</SoftButton>
            )}

            {showCalculationSection && <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mt={2}>
              <SoftBox>
                <SoftBox display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={1}>
                  {canEditField("billing_currency") && <SoftBox>
                    <SoftTypography variant="caption">Currency</SoftTypography>
                    {invoiceType === "client" ? (
                      <SoftSelect
                        options={currencyOptions}
                        value={currencyOptions.find((option) => option.code === String(form.currency_code || "USD").toUpperCase()) || {
                          value: form.currency_code || "USD",
                          label: form.currency_code || "USD",
                          code: String(form.currency_code || "USD").toUpperCase(),
                          symbol: form.currency_symbol || null,
                        }}
                        placeholder="Select currency"
                        isSearchable
                        onChange={selectCurrency}
                      />
                    ) : (
                      <SoftInput value={form.currency_code || "USD"} onChange={(event) => setField("currency_code", event.target.value.toUpperCase())} />
                    )}
                  </SoftBox>}
                  {canEditField("currency_symbol") && <SoftBox><SoftTypography variant="caption">Symbol</SoftTypography><SoftInput value={form.currency_symbol || ""} disabled={invoiceType === "client"} onChange={(event) => setField("currency_symbol", event.target.value)} /></SoftBox>}
                  {canEditField("exchange_rate") && <SoftBox>
                    <SoftTypography variant="caption">Exchange Rate</SoftTypography>
                    <SoftInput type="text" value={form.exchange_rate ?? ""} placeholder="100.0000000" inputProps={{ inputMode: "decimal" }} onChange={(event) => { const value = event.target.value; setField("exchange_rate", value); validateExchangeRate(value); }} onBlur={() => { const value = normalizeExchangeRateInput(form.exchange_rate); setField("exchange_rate", value); validateExchangeRate(value); }} />
                    {exchangeRateStatus && (
                      <SoftTypography variant="caption" display="block" sx={{ color: exchangeRateStatus.valid ? "#65b741" : "#e53935", mt: 0.35 }}>
                        {exchangeRateStatus.message}
                      </SoftTypography>
                    )}
                  </SoftBox>}
                </SoftBox>
                {canEditField("tax") && <FormControlLabel control={<Checkbox checked={Boolean(form.tax_enabled)} onChange={(event) => setField("tax_enabled", event.target.checked)} />} label="Add Tax" />}
                {invoiceType === "client" && canEditField("tax") && <SoftTypography variant="caption" display="block">Tax rule: {form.tax_enabled ? (taxRule.label || "Zero Tax") : "Tax manually removed"}</SoftTypography>}
              </SoftBox>
              <SoftBox>
                <SoftBox display="grid" gridTemplateColumns="1fr 1fr" gap={0.6} alignItems="center">
                  {(invoiceType === "client" ? ["Amount", "CGST", "SGST", "IGST", "Total Tax", "Total"] : ["Subtotal", "CGST", "SGST", "IGST", "Total Tax", "Total"])
                    .filter((label) => {
                      if (["CGST", "SGST", "IGST", "Total Tax"].includes(label)) return canEditField("tax");
                      if (label === "Subtotal") return canEditField("usd_amount");
                      return canEditField("final_amount");
                    })
                    .map((label) => {
                    const key = label.toLowerCase().replace(" ", "_");
                    const displayLabel = invoiceType === "client"
                      ? ({ CGST: "CGST (9%)", SGST: "SGST (9%)", IGST: "IGST (18%)" }[label] || label)
                      : label;
                    const editable = invoiceType !== "client" && ["cgst", "sgst", "igst"].includes(key);
                    const value = invoiceType === "client"
                      ? key === "amount" ? clientTotals.amount : key === "total_tax" ? taxTotal : key === "total" ? total : clientTotals[key] || 0
                      : key === "subtotal" ? subtotal : key === "total_tax" ? taxTotal : key === "total" ? total : form[`${key}_amount`] || 0;
                    return <SoftBox key={label} display="contents"><SoftTypography variant="button" textAlign="right">{displayLabel}:</SoftTypography>{editable ? <SoftInput type="text" inputProps={{ inputMode: "decimal" }} disabled={!form.tax_enabled} value={value} onChange={(event) => { const nextValue = event.target.value; setField(`${key}_amount`, nextValue); showDecimalFeedback(nextValue, `${displayLabel} amount`); }} onBlur={() => { const nextValue = normalizeDecimalInput(value); setField(`${key}_amount`, nextValue); showDecimalFeedback(nextValue, `${displayLabel} amount`); }} /> : <SoftInput value={currencyDisplay(value, form.currency_code, form.currency_symbol)} disabled />}</SoftBox>;
                  })}
                </SoftBox>
              </SoftBox>
            </SoftBox>}

            {message && <SoftTypography display="block" color="success" variant="button" mt={1}>{message}</SoftTypography>}
            {error && <SoftTypography display="block" color="error" variant="button" mt={1}>{error}</SoftTypography>}
            {canUpdate && <SoftButton color="success" mt={2} disabled={saving || partyLoading} onClick={submit}>{saving ? "Updating..." : "Update"}</SoftButton>}
          </Card>
        )}
      </SoftBox>
      <Footer />
      <SoftSnackbar
        color={validationAlert.color}
        icon={validationAlert.color === "success" ? "check" : "warning"}
        title={validationAlert.color === "success" ? "Valid Format" : "Invalid Format"}
        content={validationAlert.message}
        open={validationAlert.open}
        onClose={() => setValidationAlert((current) => ({ ...current, open: false }))}
        close={() => setValidationAlert((current) => ({ ...current, open: false }))}
      />
    </DashboardLayout>
  );
}

InvoiceEdit.propTypes = { invoiceType: PropTypes.oneOf(["client", "vendor"]).isRequired };

export default InvoiceEdit;
